import { LIVEKIT_PUBLIC_URL } from '@/env'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  fetchMySessions,
  fetchSessionStatus,
  joinSession,
  leaveSession,
  requestRestart,
  signalAvailability,
  signalReady,
  voteRestart
} from '../../api/conversations'
import { useConversationLogger } from '../../hooks/useConversationLogger'
import type { DataMessageEvent } from '../../hooks/useLiveKit'
import { useLiveKit } from '../../hooks/useLiveKit'
import { NectarTasksAllDone } from '../NectarTasksAllDone'
import { SessionCall } from './SessionCall'
import { SessionLobby } from './SessionLobby'
import { ConversationCompleteStep } from './steps/ConversationCompleteStep'
import { ErrorStep } from './steps/ErrorStep'
import { FindingSessionStep } from './steps/FindingSessionStep'
import { WaitingForSessionStep } from './steps/WaitingForSessionStep'

function extractError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: { message?: string; error?: string } } }).response
    return resp?.data?.message || resp?.data?.error || fallback
  }
  return fallback
}

type TaskInfo = { topic: string | null; instruction: string | null }

type Phase =
  | { step: 'finding' }
  | { step: 'waiting_for_session' }
  | {
      step: 'lobby'
      sessionId: string
      requiredCount: number
      readyCount: number
      isReady: boolean
      minDurationSec: number
      maxDurationSec: number
      topic?: string | null
      instruction?: string | null
    }
  | {
      step: 'call'
      sessionId: string
      minDurationSec: number
      maxDurationSec: number
      callStartedAt?: string
      topic?: string | null
      instruction?: string | null
    }
  | { step: 'ended'; durationSec: number }
  | { step: 'completed' }

interface RestartState {
  activeRestartId: string | null
  hasVoted: boolean
  isRequester: boolean
  requiredVotes: number
  yesCount: number
  noCount: number
}

const initialRestartState: RestartState = {
  activeRestartId: null,
  hasVoted: false,
  isRequester: false,
  requiredVotes: 0,
  yesCount: 0,
  noCount: 0
}

interface StepSessionProps {
  projectId: string
  projectName: string
  pointsAmount?: number
  onBack: () => void
  onCompleted?: () => void
}

export function StepSession({ projectId, projectName, pointsAmount, onBack, onCompleted }: StepSessionProps) {
  const log = useConversationLogger()
  const [phase, setPhase] = useState<Phase>({ step: 'finding' })
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastSessionRef = useRef<string | null>(null)

  const [restart, setRestart] = useState<RestartState>(initialRestartState)
  const [restartCount, setRestartCount] = useState(0)
  const [restartPending, setRestartPending] = useState(false)
  const [recordingStartsAt, setRecordingStartsAt] = useState<string | null>(null)
  const recordingStartsAtRef = useRef(recordingStartsAt)
  recordingStartsAtRef.current = recordingStartsAt

  const handleDataMessage = useCallback(
    (event: DataMessageEvent) => {
      switch (event.type) {
        case 'restart:requested':
          log('session', 'Restart requested', event)
          setRestart((prev) => {
            // If our own API response already set this restart, don't overwrite
            if (prev.activeRestartId === event.restartId) return prev
            return {
              activeRestartId: event.restartId,
              hasVoted: false,
              isRequester: false,
              requiredVotes: event.requiredVotes,
              yesCount: 1, // Requester auto-votes yes
              noCount: 0
            }
          })
          break
        case 'restart:voted':
          log('session', `Restart vote: ${event.vote ? 'yes' : 'no'}`, event)
          setRestart((prev) =>
            prev.activeRestartId === event.restartId
              ? {
                  ...prev,
                  yesCount: event.yesCount,
                  noCount: event.noCount
                }
              : prev
          )
          break
        case 'restart:approved':
          log('session', 'Restart approved', event)
          break
        case 'restart:executed':
          log('session', 'Restart executed — starting countdown', event)
          setRestart(initialRestartState)
          setRestartPending(false)
          if (event.recordingStartsAt) {
            setRecordingStartsAt(event.recordingStartsAt)
          }
          break
        case 'restart:rejected':
          log('session', 'Restart rejected', event)
          setRestart(initialRestartState)
          setRestartPending(false)
          toast.error('Restart declined')
          break
        case 'countdown:start':
          log('session', `Countdown started, recording at ${event.recordingStartsAt}`, event)
          setRecordingStartsAt(event.recordingStartsAt)
          break
      }
    },
    [log]
  )

  const [livekitToken, setLivekitToken] = useState('')
  const livekit = useLiveKit({
    token: livekitToken,
    serverUrl: livekitToken ? LIVEKIT_PUBLIC_URL : '',
    onDataMessage: handleDataMessage
  })

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      log('session', 'Stopping poll interval')
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [log])

  const enterCall = useCallback(
    (
      sessionId: string,
      token: string,
      session: {
        minDurationSec: number
        maxDurationSec: number
        callStartedAt: string | null
      },
      task?: TaskInfo
    ) => {
      lastSessionRef.current = sessionId
      setLivekitToken(token)
      log('phase', 'Phase -> call', {
        sessionId,
        callStartedAt: session.callStartedAt
      })
      setPhase({
        step: 'call',
        sessionId,
        minDurationSec: session.minDurationSec,
        maxDurationSec: session.maxDurationSec,
        callStartedAt: session.callStartedAt ?? undefined,
        topic: task?.topic,
        instruction: task?.instruction
      })
    },
    [log]
  )

  const enterLobby = useCallback(
    (
      sessionId: string,
      token: string,
      session: {
        requiredCount: number
        readyCount: number
        minDurationSec: number
        maxDurationSec: number
      },
      task?: TaskInfo
    ) => {
      lastSessionRef.current = sessionId
      if (token) setLivekitToken(token)
      log('phase', 'Phase -> lobby', {
        sessionId,
        hasToken: !!token,
        readyCount: session.readyCount,
        requiredCount: session.requiredCount
      })
      setPhase({
        step: 'lobby',
        sessionId,
        requiredCount: session.requiredCount,
        readyCount: session.readyCount,
        isReady: false,
        minDurationSec: session.minDurationSec,
        maxDurationSec: session.maxDurationSec,
        topic: task?.topic,
        instruction: task?.instruction
      })
    },
    [log]
  )

  const routeJoinResult = useCallback(
    (
      data:
        | {
            status: 'waiting'
            session: { id: string; requiredCount: number; joinedCount: number }
            task: TaskInfo
          }
        | {
            status: 'active'
            livekitToken: string
            session: {
              id: string
              requiredCount: number
              readyCount: number
              minDurationSec: number
              maxDurationSec: number
              startedAt: string | null
              callStartedAt: string | null
            }
            task: TaskInfo
          }
    ) => {
      if (data.status === 'active') {
        // readyCount < requiredCount = lobby (not all ready). Otherwise call.
        if (data.session.readyCount < data.session.requiredCount) {
          enterLobby(data.session.id, data.livekitToken, data.session, data.task)
        } else {
          enterCall(data.session.id, data.livekitToken, data.session, data.task)
        }
      } else {
        // "waiting" — not all joined yet, enter lobby without token
        enterLobby(
          data.session.id,
          '',
          {
            requiredCount: data.session.requiredCount,
            readyCount: 0,
            minDurationSec: 0,
            maxDurationSec: 0
          },
          data.task
        )
      }
    },
    [enterCall, enterLobby]
  )

  const doJoinSession = useCallback(
    async (sessionId: string) => {
      log('api', `POST /sessions/${sessionId}/join`)
      const data = await joinSession(sessionId)
      log('api', `JOIN result: status=${data.status}`)
      routeJoinResult(data)
    },
    [log, routeJoinResult]
  )

  const routeAvailableActive = useCallback(
    (result: {
      sessionId: string
      livekitToken: string
      session: {
        id: string
        requiredCount: number
        readyCount: number
        minDurationSec: number
        maxDurationSec: number
        startedAt: string | null
        callStartedAt: string | null
      }
      task: TaskInfo
    }) => {
      if (result.session.readyCount < result.session.requiredCount) {
        enterLobby(result.session.id, result.livekitToken, result.session, result.task)
      } else {
        enterCall(result.session.id, result.livekitToken, result.session, result.task)
      }
    },
    [enterCall, enterLobby]
  )

  const doSignalAvailability = useCallback(async (): Promise<'waiting' | 'done'> => {
    log('api', `POST /projects/${projectId}/available`)
    const result = await signalAvailability(projectId)
    log('api', `Available: status=${result.status}`)

    if (result.status === 'matched') {
      if (lastSessionRef.current === result.session.id) return 'waiting'
      await doJoinSession(result.session.id)
      return 'done'
    }

    if (result.status === 'active') {
      if (lastSessionRef.current === result.session.id) return 'waiting'
      routeAvailableActive(result)
      return 'done'
    }

    if (result.status === 'completed' || result.status === 'no_capacity') {
      log('phase', `Phase -> completed (${result.status})`)
      setPhase({ step: 'completed' })
      onCompleted?.()
      return 'done'
    }

    if (result.status === 'ineligible') {
      setError('You are not eligible for conversations in this project.')
      return 'done'
    }

    // "waiting"
    log('session', `Waiting: ${result.availableCount}/${result.requiredCount} available`)
    return 'waiting'
  }, [projectId, log, doJoinSession, routeAvailableActive])

  const findAndJoin = useCallback(
    async (opts?: { skipEndingCheck?: boolean }) => {
      log('phase', 'Phase -> finding')
      setPhase({ step: 'finding' })
      setError(null)

      try {
        log('api', `GET /sessions/mine?projectId=${projectId}`)
        const sessions = await fetchMySessions(projectId)
        const summary = sessions.data.map((s) => `${s.id.slice(0, 8)}:${s.status}`).join(', ')
        log('api', `Sessions: ${sessions.data.length} [${summary}]`)

        // Check for ENDING/PROCESSING — call is over
        if (!opts?.skipEndingCheck) {
          const endingSession = sessions.data.find((s) => s.status === 'ENDING' || s.status === 'PROCESSING')
          if (endingSession) {
            log('phase', `Phase -> ended (${endingSession.status})`)
            setPhase({ step: 'ended', durationSec: 0 })
            return
          }
        }

        // Check for WAITING/ACTIVE sessions — rejoin
        const existing = sessions.data.find((s) => s.status === 'WAITING' || s.status === 'ACTIVE')
        if (existing) {
          await doJoinSession(existing.id)
          return
        }

        // No existing session — start matchmaking
        const result = await doSignalAvailability()
        if (result === 'waiting') {
          log('phase', 'Phase -> waiting_for_session')
          setPhase({ step: 'waiting_for_session' })
        }
      } catch (err) {
        const msg = extractError(err, 'Failed to find a session')
        log('error', `findAndJoin failed: ${msg}`, err)
        setError(msg)
      }
    },
    [log, doJoinSession, doSignalAvailability]
  )

  useEffect(() => {
    findAndJoin()
  }, [findAndJoin])

  const isWaiting = phase.step === 'waiting_for_session'
  useEffect(() => {
    if (!isWaiting) return

    log('session', 'Starting availability poll (every 3s)')
    const interval = setInterval(async () => {
      try {
        const result = await doSignalAvailability()
        if (result === 'done') clearInterval(interval)
      } catch (err) {
        log('error', `Availability poll error: ${extractError(err, 'poll failed')}`)
      }
    }, 3000)

    return () => {
      log('session', 'Stopping availability poll')
      clearInterval(interval)
    }
  }, [isWaiting, doSignalAvailability, log])

  const handleReady = useCallback(async () => {
    if (phase.step !== 'lobby') return

    const { sessionId } = phase
    log('api', `POST /sessions/${sessionId}/ready`)

    try {
      const data = await signalReady(sessionId)
      log('api', `Ready: allReady=${data.allReady}`)

      if (data.allReady) {
        // Countdown will be triggered by the "countdown:start" data message
        // or picked up via polling fallback (recordingStartsAt in status)
        setPhase((prev) => (prev.step === 'lobby' ? { ...prev, isReady: true } : prev))
        if (data.recordingStartsAt) {
          setRecordingStartsAt(data.recordingStartsAt)
        }
      } else {
        setPhase((prev) => (prev.step === 'lobby' ? { ...prev, isReady: true } : prev))
      }
    } catch (err) {
      const msg = extractError(err, 'Failed to signal ready')
      log('error', msg, err)
      toast.error(msg)
    }
  }, [phase, log])

  const handleCountdownComplete = useCallback(() => {
    if (phase.step !== 'lobby') return
    log('session', 'Countdown complete, transitioning to call')
    stopPolling()
    setRecordingStartsAt(null)
    setPhase({
      step: 'call',
      sessionId: phase.sessionId,
      minDurationSec: phase.minDurationSec,
      maxDurationSec: phase.maxDurationSec,
      topic: phase.topic,
      instruction: phase.instruction
    })
  }, [phase, log, stopPolling])

  const handleLeaveLobby = useCallback(async () => {
    if (phase.step !== 'lobby') return

    const { sessionId } = phase
    log('api', `POST /sessions/${sessionId}/leave (lobby)`)

    try {
      await leaveSession(sessionId)
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status !== 400) {
        toast.error(extractError(err, 'Failed to leave session'))
        return
      }
    }

    setLivekitToken('')
    stopPolling()
    onBack()
  }, [phase, log, stopPolling, onBack])

  const handleRequestRestart = useCallback(async () => {
    if (phase.step !== 'call' || restartPending) return

    const { sessionId } = phase
    log('api', `POST /sessions/${sessionId}/restart`)
    setRestartPending(true)

    try {
      const data = await requestRestart(sessionId)
      log('api', `Restart created: ${data.id}`, data)
      setRestart({
        activeRestartId: data.id,
        hasVoted: true,
        isRequester: true,
        requiredVotes: data.requiredVotes,
        yesCount: data.yesCount,
        noCount: data.noCount
      })
    } catch (err) {
      setRestartPending(false)
      const msg = extractError(err, 'Failed to request restart')
      log('error', msg, err)
      toast.error(msg)
    }
  }, [phase, restartPending, log])

  const handleVoteRestart = useCallback(
    async (vote: boolean) => {
      if (!restart.activeRestartId || phase.step !== 'call') return

      const { sessionId } = phase
      const { activeRestartId } = restart
      log('api', `POST /sessions/${sessionId}/restart/${activeRestartId}/vote (${vote})`)

      try {
        const data = await voteRestart(sessionId, activeRestartId, vote)
        log('api', `Vote result: ${data.status}`, data)
        setRestart((prev) => ({
          ...prev,
          hasVoted: true,
          yesCount: data.yesCount,
          noCount: data.noCount
        }))
      } catch (err) {
        const msg = extractError(err, 'Failed to vote')
        log('error', msg, err)
        toast.error(msg)
      }
    },
    [restart, phase, log]
  )

  const lobbySessionId = phase.step === 'lobby' ? phase.sessionId : null

  useEffect(() => {
    if (!lobbySessionId) {
      stopPolling()
      return
    }

    const sessionId = lobbySessionId
    log('session', `Starting lobby poll for ${sessionId} (every 3s)`)

    const poll = async () => {
      try {
        const status = await fetchSessionStatus(sessionId)

        if (status.status === 'CANCELLED') {
          stopPolling()
          setLivekitToken('')
          log('session', 'Session cancelled, re-entering matchmaking')
          toast.info("Your match didn't connect. Finding a new one...")
          findAndJoin({ skipEndingCheck: true })
          return
        }

        if (status.status === 'FAILED') {
          stopPolling()
          setLivekitToken('')
          setError('Session failed. Please try again.')
          return
        }

        // Session ACTIVE but no token yet — re-join to get it
        if (status.status === 'ACTIVE' && !livekitToken) {
          log('session', 'Session ACTIVE but no token, re-joining')
          stopPolling()
          await doJoinSession(sessionId)
          return
        }

        // All participants ready — pick up recordingStartsAt for countdown
        if (status.status === 'ACTIVE' && status.readyCount >= status.requiredCount) {
          // Countdown fallback: always pick up recordingStartsAt before
          // checking callStartedAt, so the first-to-click-ready participant
          // sees the countdown even if the LiveKit data message was missed.
          if (status.recordingStartsAt && !recordingStartsAt) {
            log('session', 'Picked up recordingStartsAt from polling fallback')
            setRecordingStartsAt(status.recordingStartsAt)
            // Let the countdown timer handle the transition to call
            setPhase((prev) =>
              prev.step === 'lobby' ? { ...prev, readyCount: status.readyCount, isReady: true } : prev
            )
            return
          }

          // If callStartedAt is already set and countdown is finished,
          // recording has started — go to call. Don't transition while
          // countdown is still in progress (egress starts before countdown ends).
          if (status.callStartedAt && !recordingStartsAt) {
            log('session', 'All ready and recording started, transitioning to call')
            stopPolling()
            setPhase((prev) =>
              prev.step === 'lobby'
                ? {
                    step: 'call',
                    sessionId,
                    minDurationSec: status.minDurationSec,
                    maxDurationSec: status.maxDurationSec,
                    callStartedAt: status.callStartedAt ?? undefined,
                    topic: prev.topic,
                    instruction: prev.instruction
                  }
                : prev
            )
            return
          }

          setPhase((prev) => (prev.step === 'lobby' ? { ...prev, readyCount: status.readyCount, isReady: true } : prev))
          return
        }

        setPhase((prev) => {
          if (prev.step !== 'lobby') return prev
          return {
            ...prev,
            readyCount: status.readyCount,
            minDurationSec: status.minDurationSec || prev.minDurationSec,
            maxDurationSec: status.maxDurationSec || prev.maxDurationSec
          }
        })

        // Sync restart state from status (fallback for missed data messages)
        setRestartCount(status.restartCount)
      } catch (err) {
        log('error', `Lobby poll error: ${extractError(err, 'status check failed')}`, err)
      }
    }

    poll()
    const interval = setInterval(poll, 3000)
    pollRef.current = interval
    return () => {
      clearInterval(interval)
      if (pollRef.current === interval) pollRef.current = null
    }
  }, [lobbySessionId, livekitToken, stopPolling, log, doJoinSession, findAndJoin, recordingStartsAt])

  const callSessionId = phase.step === 'call' ? phase.sessionId : null

  useEffect(() => {
    if (!callSessionId) return

    const sessionId = callSessionId
    const interval = setInterval(async () => {
      try {
        const status = await fetchSessionStatus(sessionId)

        // Only sync restartCount when not in countdown (countdown handles its own reset)
        if (!recordingStartsAtRef.current) {
          setRestartCount(status.restartCount)
        }

        // Sync callStartedAt after a restart so the timer resets
        if (status.callStartedAt) {
          setPhase((prev) =>
            prev.step === 'call' && prev.callStartedAt !== status.callStartedAt
              ? {
                  ...prev,
                  callStartedAt: status.callStartedAt ?? undefined
                }
              : prev
          )
        }

        // Sync active restart request as fallback for missed data messages
        if (status.activeRestartRequest && !restart.activeRestartId) {
          const req = status.activeRestartRequest
          log('session', 'Detected active restart from status poll', req)
          setRestart({
            activeRestartId: req.id,
            hasVoted: false,
            isRequester: req.isRequester,
            requiredVotes: req.requiredVotes,
            yesCount: req.yesCount,
            noCount: req.noCount
          })
        } else if (!status.activeRestartRequest && restart.activeRestartId) {
          // Restart resolved but we missed the event
          setRestart(initialRestartState)
          setRestartPending(false)
        }
      } catch {
        // Ignore poll errors during call
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [callSessionId, restart.activeRestartId, log])

  const handleRestartCountdownComplete = useCallback(() => {
    log('session', 'Restart countdown complete, resetting timer')
    setRecordingStartsAt(null)
    setRestartCount((c) => c + 1)
    toast.success('Recording restarted')
  }, [log])

  const handleCallEnded = useCallback(
    async (durationSec: number) => {
      log('phase', `Phase -> ended (duration: ${durationSec}s)`)
      setLivekitToken('')
      setRestart(initialRestartState)

      try {
        const result = await signalAvailability(projectId)
        if (result.status === 'completed' || result.status === 'no_capacity') {
          log('phase', `Phase -> completed (${result.status} after call)`)
          setPhase({ step: 'completed' })
          onCompleted?.()
          return
        }
      } catch {
        // If check fails, fall through to ended screen
      }

      setPhase({ step: 'ended', durationSec })
    },
    [log, projectId, onCompleted]
  )

  if (phase.step === 'finding' && !error) {
    return <FindingSessionStep />
  }

  if (phase.step === 'waiting_for_session') {
    return <WaitingForSessionStep onBack={onBack} />
  }

  if (error) {
    return <ErrorStep error={error} onBack={onBack} findAndJoin={findAndJoin} />
  }

  if (phase.step === 'lobby') {
    return (
      <SessionLobby
        requiredCount={phase.requiredCount}
        readyCount={phase.readyCount}
        isReady={phase.isReady}
        topic={phase.topic ?? undefined}
        instruction={phase.instruction ?? undefined}
        onReady={handleReady}
        onLeave={handleLeaveLobby}
        connectionState={livekit.connectionState}
        participants={livekit.participants}
        isMuted={livekit.isMuted}
        toggleMute={livekit.toggleMute}
        hasLiveKit={!!livekitToken}
        recordingStartsAt={recordingStartsAt}
        onCountdownComplete={handleCountdownComplete}
      />
    )
  }

  if (phase.step === 'call') {
    return (
      <SessionCall
        sessionId={phase.sessionId}
        minDurationSec={phase.minDurationSec}
        maxDurationSec={phase.maxDurationSec}
        callStartedAt={phase.callStartedAt}
        topic={phase.topic ?? undefined}
        instruction={phase.instruction ?? undefined}
        onEnded={handleCallEnded}
        connectionState={livekit.connectionState}
        participants={livekit.participants}
        isMuted={livekit.isMuted}
        toggleMute={livekit.toggleMute}
        restart={restart}
        restartCount={restartCount}
        restartPending={restartPending}
        onRequestRestart={handleRequestRestart}
        onVoteRestart={handleVoteRestart}
        recordingStartsAt={recordingStartsAt}
        onRestartCountdownComplete={handleRestartCountdownComplete}
      />
    )
  }

  if (phase.step === 'completed') {
    return <NectarTasksAllDone name={projectName} pointsAmount={pointsAmount} />
  }

  return <ConversationCompleteStep phase={phase as Extract<Phase, { step: 'ended' }>} onBack={onBack} />
}
